import {AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {ProgressButtonOptions} from '@service/models/interfaces/progress-button.interface';
import {AddressModel} from '@service/models';
import {CountryList, StateList} from '@service/enum/process-state.enum';
import {AppConfigService} from '@service/config/app-config.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ContentService} from '../../../../constants/content.service';
import {AbstractControl, NgForm, NgModel, ValidationErrors} from "@angular/forms";
import {MatSelectChange} from "@angular/material/select";
import { ApplicationService } from '@service/services';
import { environment } from '@env/environment';

@Component({
    selector: 'm-edit-address',
    templateUrl: './edit-address.component.html',
    styleUrls: ['./edit-address.component.scss']
})
export class EditAddressComponent implements OnInit, AfterViewInit {
    @ViewChild('streetAddress', {read: NgModel}) streetAddress: NgModel;
    @ViewChild('zipCode', {read: NgModel}) zipCode: NgModel;
    @ViewChild('f') addressForm: NgForm;
    barButtonOptions: ProgressButtonOptions = {
        active: false,
        text: 'Update',
        buttonColor: 'primary',
        // @ts-ignore
        flat: true,
        stroked: false,
        disabled: false
    };
    address: AddressModel;
    countryList = CountryList;
    appConfig: any = AppConfigService.config;
    supportedCountries: any[] = [];
    stateList = StateList;
    isMailingAddress: boolean;
    selectedState: any;
    zipErrorMessage: string = '';
    addressTypeName = 'streetAddress';

    constructor(
        public dialogRef: MatDialogRef<EditAddressComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public contentService: ContentService,
        private applicationService: ApplicationService,
        private cdr: ChangeDetectorRef
    ) {
        this.address = data.address;
        this.isMailingAddress = data.isMailingAddress;
        if (data.addressTypeName) {
            this.addressTypeName = data.addressTypeName;
        }

        if (this.appConfig.supportedCountries) {
            if (this.appConfig.supportedCountries === 'all') {
                this.supportedCountries = this.countryList;
            } else {
                this.supportedCountries = this.countryList.filter(country => this.appConfig.supportedCountries.indexOf(country.alpha2) > -1);
            }
        }

        if (this.supportedCountries.length === 0) {
            this.supportedCountries.push({alpha2: 'US', alpha3: 'USA', name: 'United States of America'});
        }
    }

    ngOnInit() {
        this.dialogRef.keydownEvents().subscribe(event => {
            if (event.key === 'Escape') {
                this.onSubmit({isCancel: true});
            }
        });

        this.dialogRef.backdropClick().subscribe(event => {
            this.onSubmit({isCancel: true});
        });
    }

    ngAfterViewInit() {
        this.streetAddress.control.addValidators((control: AbstractControl): ValidationErrors | null => {
            let error = null;
            if (!this.isMailingAddress && control.value) {
                 if (this.address.country === 'USA' && !this.selectedState?.excludeStreetNumberValidation) {
                    error = !this.isFirstCharNumber(control.value.trim()) ? {streetNumber: true} : error;
                }
            }
            return error;
        })
    }

    isFirstCharNumber(str: string) {
        const firstChar = (str || '').charAt(0);
        return !isNaN(Number(firstChar));
    }

    onCountryChanged(event: MatSelectChange) {
        const country = this.address.country;
        this.selectedState = null;
        this.address.fullAddress = null;
        this.clearZipError();
        this.addressForm.resetForm({addressCountry: country});
    }

    onSubmit(options?: { isCancel?: boolean}): void  {
        const isCancel = options?.isCancel;
        if (this.barButtonOptions.active) {
            return;
        }
        if (isCancel) {
            this.dialogRef.close();
            return;
        }
        this.barButtonOptions.active = true;
        this.zipErrorMessage = '';

        if (this.address.country === 'USA') {
            this.applicationService.validateZipCode(this.address.zipCode).subscribe({
                next: (response: any) => {
                    this.barButtonOptions.active = false;
                       if ((!response.hits || response.hits.length <= 0) && (environment.production || !this.appConfig?.skipTestAddressValidation)) {
                        this.zipErrorMessage ='Invalid ZIP code. Please enter a valid ZIP code.';
                        this.zipCode.control.setErrors({ zipCode: this.zipErrorMessage });
                        return;
                    }

                    const matchingEntry = response.hits.find(
                        (entry: any) =>
                            entry.City.toLowerCase() === this.address.city.toLowerCase() &&
                            entry.ST.toLowerCase() === this.address.state.toLowerCase()
                    );

                   
                    if (!matchingEntry && (environment.production || !this.appConfig.skipTestAddressValidation)) {
                        this.zipErrorMessage = 'ZIP code does not match the given state and city. Please check.';
                        this.zipCode.control.setErrors({ zipCode: this.zipErrorMessage });
                        return;
                    }
                    this.zipCode.control.setErrors(null);
                    const {streetAddress, city, state, country, zipCode} = this.address;
                    this.address.fullAddress = `${streetAddress}, ${city}, ${state}, ${zipCode}, ${country}`;
                    this.dialogRef.close(this.address);
                },
                error: () => {
                    this.barButtonOptions.active = false;
                    this.zipErrorMessage = 'An error occurred while validating the ZIP code. Please try again.';
                    this.zipCode.control.setErrors({ zipCode: this.zipErrorMessage });
                }
            });
        } else {
            const {streetAddress, city, state, country, zipCode} = this.address;
            this.address.fullAddress = `${streetAddress}, ${city}, ${state}, ${zipCode}, ${country}`;
            this.dialogRef.close(this.address);
        }
    }

    stateChanged(event: MatSelectChange) {
        this.selectedState = StateList.find(state => state.code === event.value);
        this.streetAddress.control.updateValueAndValidity();
        this.clearZipError();
    }

    clearZipError(): void {
        this.zipErrorMessage = '';
        if (this.zipCode && this.zipCode.control) {
            this.zipCode.control.setErrors(null);
        }
    }

}
